import { ChartJS, EmptyBox } from '@blockanalitica/ui';
import type {
  ChartConfig,
  ChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import _ from 'lodash';
import { ReactNode, useMemo } from 'react';
import { useTheme } from 'styled-components';

export type SimpleChartData = {
  [field: string]: number | string | null;
};

export type SimpleChartDataset = {
  xField: string;
  yField: string;
  data?: SimpleChartData[];
  label?: string;
  backgroundColor?: string;
};

export interface JustBarChartProps {
  data?: SimpleChartData[];
  datasets: SimpleChartDataset[];
  config?: ChartConfig;
  emptyComponent?: ReactNode;
}

export default function JustBarChart({
  data,
  datasets,
  config,
  emptyComponent = <EmptyBox />
}: JustBarChartProps) {
  const theme = useTheme();

  const chartDatasets: ChartDataset[] = useMemo(() => {
    return datasets.map((ds) => {
      const datasetData = ds.data ?? data;
      if (!datasetData || _.isEmpty(datasetData)) {
        return {};
      }
      const color = ds.backgroundColor || '#888';
      return {
        label: ds.label,
        data: datasetData.map((item) => {
          const rawY = item[ds.yField || 'total'];
          return {
            x: item[ds.xField || 'date'],
            // Backend serializes financial values as strings; coerce so the
            // linear axis scales correctly and tooltips don't show NaN.
            y: rawY === null || rawY === undefined ? null : Number(rawY)
          };
        }),
        backgroundColor: color,
        borderColor: color
      };
    });
  }, [data, datasets]);

  const chartConfig: ChartConfig = useMemo(() => {
    const cData = chartDatasets.every((v) => _.isEmpty(v))
      ? null
      : { datasets: chartDatasets };

    const defaultConfig = {
      type: 'bar',
      data: cData,
      options: {
        aspectRatio: 2.5,
        animation: false,
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: { display: false }
        },
        scales: {
          x: {
            display: false,
            type: 'category',
            grid: { display: false },
            border: { display: false }
          },
          y: {
            display: false,
            type: 'linear',
            ticks: {
              // @ts-ignore — theme.colors typing gap shared across the codebase
              color: theme.colors?.muted,
              format: { notation: 'compact' }
            },
            grid: { display: false },
            border: { display: false }
          }
        }
      }
    };

    return _.merge(defaultConfig, config);
  }, [chartDatasets, config, theme.colors.muted]);

  return (
    <>{chartConfig.data ? <ChartJS config={chartConfig} /> : emptyComponent}</>
  );
}
